import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputPlusMinus } from '~/features/basket/components/BasketList/components/InputPlusMinus';
import { useTranslation } from '~/shared/utils/translation';
import Spacer from '~/theme/design-system/components/Spacer';
import { MakeReservationStep } from './MakeReservationStep';
import { ReservationInfoMessage } from './ReservationInfoMessage';
import { StoreListStep } from './StoreListStep';
import { SuccessStep } from './SuccessStep';
import { useReservationFlowStore } from './useReservationFlowStore';
import { useReservationStores } from './useReservationStores';
import { useReservationStoreSearch } from './useReservationStoreSearch';
import { useEvents } from '~/shared/hooks/useEvents';
import {
    StorePickupContext,
    StorePickupOption,
} from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useFrame } from '~/shared/utils';
import { InfoDrawer } from '~/shared/InfoDrawer';
import { ReservationProductCard } from './ReservationProductCard';
import type { LocationSearchPayload, ReservationStoreCtaIntent } from './types';
import { canReserveFromStore, countStoreAvailability } from './utils/reservationStoreStatus';
import { MiniBasketStoreSuggestionStatus, useBasket } from '~/features/basket';
import { transformSalesforceProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { ShopperProductsTypes } from 'commerce-sdk-isomorphic';

type Props = {
    product: SalesforceProduct;
    featuredImage: ShopperProductsTypes.Image | undefined;
    showReservationDrawer: boolean;
    setShowReservationDrawer: (show: boolean) => void;
};

export const ReservationDrawer = ({
    product,
    featuredImage,
    showReservationDrawer,
    setShowReservationDrawer,
}: Props) => {
    const [isStoresWithStockOnly, setIsStoresWithStockOnly] = useState(false);
    const [pendingStoreCtaId, setPendingStoreCtaId] = useState<string | null>(null);

    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const {
        addItemToBasket,
        setMiniBasketStoreSuggestionError,
        toggleMiniBasket,
        setSuggestedStore,
        setMiniBasketStoreSuggestionStatus,
    } = useBasket();
    const {
        storePickupDrawerOpenEvent,
        storePickupDrawerSelectEvent,
        storePickupDrawerToggleEvent,
        storePickupDrawerAccordionEvent,
        storePickupDrawerQuantityEvent,
        storePickupDrawerLocationSearchEvent,
        usedStorePickupOptionEvent,
    } = useEvents(frame);

    const step = useReservationFlowStore((state) => state.step);
    const selectedStore = useReservationFlowStore((state) => state.selectedStore);
    const quantity = useReservationFlowStore((state) => state.quantity);
    const increaseQuantity = useReservationFlowStore((state) => state.increaseQuantity);
    const decreaseQuantity = useReservationFlowStore((state) => state.decreaseQuantity);
    const setQuantity = useReservationFlowStore((state) => state.setQuantity);
    const quantityWarningLatched = useReservationFlowStore((state) => state.quantityWarningLatched);
    const latchQuantityWarning = useReservationFlowStore((state) => state.latchQuantityWarning);
    const chooseStore = useReservationFlowStore((state) => state.chooseStore);
    const goBackToStoreList = useReservationFlowStore((state) => state.goBackToStoreList);
    const resetReservationForm = useReservationFlowStore((state) => state.reset);
    const reservationNumber = useReservationFlowStore((state) => state.reservationNumber);

    const assumedVariant = useMemo(
        () => ({
            productId: product.id,
            orderable: product.inventory?.orderable,
        }),
        [product.id],
    );

    const {
        allStores,
        defaultStores,
        isLoading: isLoadingStores,
    } = useReservationStores({ pid: product?.id });

    const hasFiredOpenEventRef = useRef(false);

    useEffect(() => {
        if (hasFiredOpenEventRef.current || isLoadingStores) return;
        hasFiredOpenEventRef.current = true;

        storePickupDrawerOpenEvent({
            context: StorePickupContext.PdpDrawer,
            option: StorePickupOption.ClickAndReserve,
            ...countStoreAvailability(allStores ?? [], product),
        });
    }, [isLoadingStores, storePickupDrawerOpenEvent]);

    const trackLocationSearch = useCallback((payload: LocationSearchPayload) => {
        storePickupDrawerLocationSearchEvent({
            context: StorePickupContext.PdpDrawer,
            option: StorePickupOption.ClickAndReserve,
            ...payload,
        });
    }, []);

    const {
        searchQuery,
        setSearchQuery,
        storeSearchInputRef,
        nearbyStores,
        searchError,
        toggleGeoLocation,
        isGeoActive,
        isLocating,
    } = useReservationStoreSearch({
        allStores,
        defaultStores,
        product,
        trackLocationSearch,
    });

    const handleStoresWithStockToggle = useCallback(
        (checked: boolean) => {
            setIsStoresWithStockOnly(checked);

            storePickupDrawerToggleEvent({
                context: StorePickupContext.PdpDrawer,
                option: StorePickupOption.ClickAndReserve,
                mode: checked ? 'enabled' : 'disabled',
                ...countStoreAvailability(allStores ?? [], product),
            });
        },
        [allStores, product],
    );

    const handleAccordionOpen = useCallback((shopId: string, index: number) => {
        storePickupDrawerAccordionEvent({
            context: StorePickupContext.PdpDrawer,
            option: StorePickupOption.ClickAndReserve,
            shopId,
            index,
        });
    }, []);

    const confirmAndClose = useCallback(() => {
        setShowReservationDrawer(false);
        resetReservationForm();
    }, [resetReservationForm]);

    const toggleReservationDrawer = useCallback(() => {
        if (pendingStoreCtaId) return;

        resetReservationForm();
        setShowReservationDrawer(!showReservationDrawer);
    }, [showReservationDrawer, pendingStoreCtaId]);

    const handleStoreCtaClick = useCallback(
        async (intent: ReservationStoreCtaIntent) => {
            storePickupDrawerSelectEvent({
                context: StorePickupContext.PdpDrawer,
                option:
                    intent.type === 'reserveInStore'
                        ? StorePickupOption.ClickAndReserve
                        : StorePickupOption.ClickAndCollect,
                shopId: intent.store.id,
            });

            if (intent.type === 'reserveInStore') {
                chooseStore(intent.store);
                return;
            }

            const selectedQuantity = quantity;

            setPendingStoreCtaId(intent.store.id);

            setMiniBasketStoreSuggestionError(null);

            setShowReservationDrawer(false);

            setMiniBasketStoreSuggestionStatus(MiniBasketStoreSuggestionStatus.LOADING);

            resetReservationForm();

            toggleMiniBasket(true);

            try {
                if (product) {
                    const addedToBasket = await addItemToBasket(
                        transformSalesforceProductToDataLayer(product),
                        assumedVariant,
                        selectedQuantity,
                        StorePickupOption.ClickAndCollect,
                    );

                    if (addedToBasket) {
                        usedStorePickupOptionEvent({
                            context: StorePickupOption.ClickAndCollect,
                            shopId: intent.store.id,
                        });
                    }
                }

                try {
                    await setSuggestedStore({
                        storeId: intent.store.id,
                        storeName: intent.store.name,
                        address1: intent.store.address1,
                        address2: intent.store.address2,
                        city: intent.store.city,
                        postalCode: intent.store.postalCode ?? '',
                        countryCode: 'DK',
                        phone: intent.store.phone ?? '',
                    });
                    setMiniBasketStoreSuggestionStatus(MiniBasketStoreSuggestionStatus.SUCCESS);
                } catch (error) {
                    console.warn('Could not save suggested store', error);
                    setMiniBasketStoreSuggestionError(
                        translate('message.storeNotSelectedForCheckout'),
                    );
                    setMiniBasketStoreSuggestionStatus(MiniBasketStoreSuggestionStatus.ERROR);
                }
            } finally {
                setPendingStoreCtaId(null);
            }
        },
        [product?.id, quantity, toggleMiniBasket],
    );

    useEffect(() => {
        return () => {
            resetReservationForm();
        };
    }, [resetReservationForm]);

    useEffect(() => {
        if (step === 'makeReservation' && !selectedStore) {
            goBackToStoreList();
        }

        if (step === 'success' && !reservationNumber) {
            resetReservationForm();
        }
    }, [reservationNumber, selectedStore, step]);

    const successTitle = useMemo(() => translate('pdp.reserveInStoreConfirmation'), []);
    const defaultTitle = useMemo(() => translate('pdp.reserveInStore'), []);
    const ctaTitle = useMemo(() => translate('checkout.consent.button.text'), []);

    // True when the current quantity has pushed at least one reservable store past its click-and-
    // reserve stock, so that store no longer shows its reserve CTA. This is the exact trigger for
    // the quantity warning.
    const quantityRemovesReserveCta = Boolean(
        allStores?.some(
            (store) => canReserveFromStore(store) && !canReserveFromStore(store, quantity),
        ),
    );

    useEffect(() => {
        if (quantityRemovesReserveCta && !quantityWarningLatched) {
            latchQuantityWarning();
        }
    }, [quantityRemovesReserveCta, quantityWarningLatched]);

    // Fall back to the store list if the flow state is missing data required for the current step.
    const showStoreList =
        step === 'storeList' ||
        (step === 'makeReservation' && !selectedStore) ||
        (step === 'success' && !reservationNumber);

    const showQuantityWarning = showStoreList && showReservationDrawer && quantityWarningLatched;

    const reserveDisabledMessage =
        product.c_isProductClickandReserveDisabled && translate('pdp.reserveDisabledMessage');

    // In step 2 the store is locked in, so cap the quantity at what that store can actually
    // reserve.
    const reserveMaxQuantity =
        step === 'makeReservation' ? selectedStore?.c_stock_level_clickreserve ?? 0 : 0;
    const hasReserveMax = reserveMaxQuantity >= 1;

    const quantitySelector = useMemo(() => {
        if (!product) return null;

        const getCurrentQuantity = () => useReservationFlowStore.getState().quantity;

        const fireQuantityEvent = (
            newQuantity: number,
            method: 'plus-btn' | 'minus-btn' | 'manual_input',
        ) => {
            storePickupDrawerQuantityEvent({
                context: StorePickupContext.PdpDrawer,
                option: StorePickupOption.ClickAndReserve,
                oldQuantity: getCurrentQuantity(),
                newQuantity,
                method,
                ...countStoreAvailability(allStores ?? [], product, newQuantity),
            });
        };

        return (
            <InputPlusMinus
                productName={product.productName}
                disableMinus={quantity <= 1}
                disablePlus={hasReserveMax && quantity >= reserveMaxQuantity}
                maxAvailableQuantity={hasReserveMax ? reserveMaxQuantity : undefined}
                handlePlus={() => {
                    fireQuantityEvent(getCurrentQuantity() + 1, 'plus-btn');
                    increaseQuantity();
                }}
                handleMinus={() => {
                    if (getCurrentQuantity() <= 1) return;
                    fireQuantityEvent(getCurrentQuantity() - 1, 'minus-btn');
                    decreaseQuantity();
                }}
                defaultValue={quantity}
                onChange={(newQuantity) => {
                    if (!newQuantity || newQuantity === getCurrentQuantity()) return;
                    fireQuantityEvent(newQuantity, 'manual_input');
                    setQuantity(newQuantity);
                }}
                decreaseLabel="Reducer antal med 1"
                increaseLabel="Forøg antal med 1"
            />
        );
    }, [quantity, product, allStores, reserveMaxQuantity, hasReserveMax]);

    // Early return if the reservation is successful
    if (step === 'success' && reservationNumber) {
        return (
            <InfoDrawer
                title={successTitle}
                open={showReservationDrawer}
                close={confirmAndClose}
                ctaTitle={ctaTitle}
            >
                <SuccessStep reservationNumber={reservationNumber} onClose={confirmAndClose} />
            </InfoDrawer>
        );
    }

    return (
        <InfoDrawer
            title={defaultTitle}
            open={showReservationDrawer}
            close={toggleReservationDrawer}
            ctaTitle={ctaTitle}
        >
            {product && (
                <ReservationProductCard
                    product={product}
                    image={featuredImage}
                    quantitySelector={quantitySelector}
                />
            )}
            {reserveDisabledMessage && (
                <ReservationInfoMessage>{reserveDisabledMessage}</ReservationInfoMessage>
            )}
            {showQuantityWarning ? (
                <ReservationInfoMessage announceToScreenReader>
                    {translate('message.quantityExceedsReservationStock')}
                </ReservationInfoMessage>
            ) : (
                <Spacer />
            )}
            {showStoreList && showReservationDrawer && (
                <StoreListStep
                    product={product}
                    isStoresWithStockOnly={isStoresWithStockOnly}
                    setIsStoresWithStockOnly={handleStoresWithStockToggle}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    storeSearchInputRef={storeSearchInputRef}
                    isLoadingStores={isLoadingStores}
                    isGeoActive={isGeoActive}
                    isLocating={isLocating}
                    toggleGeoLocation={toggleGeoLocation}
                    searchError={searchError}
                    nearbyStores={nearbyStores}
                    quantity={quantity}
                    pendingStoreCtaId={pendingStoreCtaId}
                    onStoreCtaClick={handleStoreCtaClick}
                    onAccordionOpen={handleAccordionOpen}
                />
            )}
            {step === 'makeReservation' && selectedStore && product && (
                <MakeReservationStep
                    product={product}
                    selectedStore={selectedStore}
                    onBack={goBackToStoreList}
                />
            )}
        </InfoDrawer>
    );
};
